import { queryClient } from "@istari-frontend/api-client";
import { getEnv, reportAsyncError } from "@istari-frontend/shared";
import * as Sentry from "@sentry/react";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "react-router";

import { BranchingProvider } from "./branching";
import "./index.css";
import { addRumError, initRum, rumEligible } from "./observability/rum";
import { router } from "./router";

initRum().catch((error) => {
  // reportAsyncError only reaches Sentry when it's enabled AND a user is
  // logged in, but initRum runs unconditionally — keep a console trail so a
  // misconfig is visible on logged-out pages and Sentry-off envs too.
  if (!import.meta.env.DEV) console.error("[init-rum]", error);
  reportAsyncError("init-rum", error);
});

if (import.meta.env.DEV) {
  void import("./notifications/feature/dev-helper");
}

// zitadel user
const userStr = window.localStorage.getItem("istari-user");
const user = userStr ? JSON.parse(userStr) : null;
const userId = user?.profile?.sub;
const userEmail = user?.profile?.email;

// Where RUM will actually run it owns performance tracing and session replay;
// Sentry keeps only error capture there so reportAsyncError still lands.
// rumEligible checks the same gates initRum enforces (flag, webdriver,
// credentials), so the handoff can't strand a session with neither tracer.
const rumOwnsTracing = rumEligible();

const sentryTracingOptions = rumOwnsTracing
  ? { integrations: [], tracesSampleRate: 0, replaysSessionSampleRate: 0, replaysOnErrorSampleRate: 0 }
  : {
      integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
      tracesSampleRate: 0.25,
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
    };

// do not track if sentry flag is off and unlogged in flow
if (getEnv("VITE_SENTRY_ENABLED") === "true" && userId && userEmail) {
  Sentry.init({
    dsn: getEnv("VITE_SENTRY_DSN"),
    environment: `${getEnv("VITE_NODE_ENV")}`,
    initialScope: {
      user: { id: userId, email: userEmail },
    },
    ...sentryTracingOptions,
  });
}

// onCaughtError is the essential hook: boundary-caught render errors are
// swallowed by default and never reach window.onerror, so neither SDK would
// see them. onUncaughtError/onRecoverableError do reach the global handlers
// via reportError(); they're overridden to attach componentStack + handling.
const sentryReactErrorHandler = Sentry.reactErrorHandler();

function reportReactError(handling: "boundary" | "unhandled" | "recoverable") {
  return (error: unknown, errorInfo: React.ErrorInfo) => {
    if (import.meta.env.DEV) {
      console.error(`[react:${handling}]`, error, errorInfo.componentStack);
    }
    sentryReactErrorHandler(error, errorInfo);
    try {
      addRumError(error, { source: "react", handling, componentStack: errorInfo.componentStack });
    } catch {
      // A failing RUM reporter must never suppress the Sentry capture above.
    }
  };
}

const rootElement = document.getElementById("root");
if (rootElement) {
  ReactDOM.createRoot(rootElement, {
    onCaughtError: reportReactError("boundary"),
    onUncaughtError: reportReactError("unhandled"),
    onRecoverableError: reportReactError("recoverable"),
  }).render(
    <QueryClientProvider client={queryClient}>
      <BranchingProvider>
        <React.Suspense
          fallback={
            <div data-testid="SuspenseFallback-loading-spinner" role="status" aria-live="polite">
              <span className="sr-only">Loading</span>
            </div>
          }
        >
          <RouterProvider router={router} />
        </React.Suspense>
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="top-left" />
      </BranchingProvider>
    </QueryClientProvider>,
  );
}
